/**
 * AskQuestionModal Component
 *
 * Modal dialog for composing and sending questions to the Clinical Quality team.
 * Includes a textarea for the question, a read-only context summary showing what
 * patient/measure data will be included in the email, and send/cancel buttons.
 *
 * Rendered as a separate Dialog (not nested inside PatientModal) to avoid
 * shadcn Dialog nesting issues.
 */

import { useState } from 'react'
import { Loader2, Copy, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { askQuestion } from '@/services/askQuestionService'
import type { QuestionContext, PatientMeasureItem } from '@/lib/equip-types'
import { getMeasureDisplayName } from '@/lib/equip-utils'

interface AskQuestionModalProps {
  open: boolean
  onClose: () => void
  context: QuestionContext
  patientMeasures?: PatientMeasureItem[]
}

function getMeasureStatusLabel(measure: PatientMeasureItem): string {
  if (!measure.isEligible) return 'N/A'
  return measure.isMet ? 'Met' : 'Unmet'
}

export function AskQuestionModal({
  open,
  onClose,
  context,
  patientMeasures,
}: AskQuestionModalProps) {
  const [questionText, setQuestionText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopyContext = () => {
    const lines = [
      `Patient: ${context.identifier}${context.facilityName ? ` | ${context.facilityName}` : ''}`,
      `Quarter: Q${context.quarter} ${context.year}`,
      `From: ${context.providerName} (${context.providerEmail})`,
    ]
    if (patientMeasures && patientMeasures.length > 0) {
      lines.push(
        `Measures: ${patientMeasures
          .map((m) => `${getMeasureDisplayName(m.measureName)} (${getMeasureStatusLabel(m)})`)
          .join(', ')}`
      )
    }
    navigator.clipboard.writeText(lines.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleClose = () => {
    if (isSending) return
    setQuestionText('')
    onClose()
  }

  const handleSend = async () => {
    setIsSending(true)
    const success = await askQuestion(context, questionText, patientMeasures)
    setIsSending(false)
    if (success) {
      setQuestionText('')
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ask a Question</DialogTitle>
          <DialogDescription>
            Your question will be sent to the Clinical Quality team along with
            this patient's measure data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Textarea
            placeholder="Type your question here..."
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            rows={4}
            disabled={isSending}
            className="resize-none"
          />

          {/* Context summary - what will be included in the email */}
          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-medium text-foreground text-xs">Included in email:</span>
              <button
                onClick={handleCopyContext}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <><Check className="h-3 w-3" /> Copied</>
                ) : (
                  <><Copy className="h-3 w-3" /> Copy</>
                )}
              </button>
            </div>
            <div>
              Patient: {context.identifier}
              {context.facilityName && ` | ${context.facilityName}`}
            </div>
            <div>Quarter: Q{context.quarter} {context.year}</div>
            <div>From: {context.providerName} ({context.providerEmail})</div>
            {patientMeasures && patientMeasures.length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t border-border/50">
                Measures:{' '}
                {patientMeasures
                  .map(
                    (m) =>
                      `${getMeasureDisplayName(m.measureName)} (${getMeasureStatusLabel(m)})`
                  )
                  .join(', ')}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!questionText.trim() || isSending}
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Question'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
